import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateActivityCategoryService } from '@wilson/activity-categories/data';
import { ActivitiesGateway } from '@wilson/api/gateway';
import { Base } from '@wilson/base';
import {
  ActivityCategory,
  RoleAction,
  RolePermissionSubject,
} from '@wilson/interfaces';
import {
  catchError,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  tap,
} from 'rxjs';
import { ActivityCategorySelectionService } from './activity-category-selection.service';
import { ActivityCategoryConfirmModalComponent } from './activity-category-confirm-modal/activity-category-confirm-modal.component';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';
import { AbilityServiceSignal } from '@casl/angular';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
  selector: 'wilson-activity-category-selection',
  templateUrl: './activity-category-selection.component.html',
  styleUrls: ['./activity-category-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ActivityCategorySelectionComponent implements OnInit {
  private activitiesService = inject(ActivitiesGateway);
  private activityCategorySelectionService = inject(
    ActivityCategorySelectionService,
  );
  private messageService = inject(NzMessageService);
  private translate = inject(TranslateService);
  private readonly translateActivityCategoryService = inject(
    TranslateActivityCategoryService,
  );
  private nzModalService = inject(NzModalService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Output()
  formDirty = new EventEmitter<boolean>();

  availableCategories: ActivityCategory[] = [];

  // What the server has saved — used as the baseline for computing diffs.
  savedCategories: ActivityCategory[] = [];

  // What the user currently has selected in the UI (drives ngModel on nz-select).
  selectedCategories: ActivityCategory[] = [];

  isLoading = false;
  isSaving = false;

  listToUpdate: {
    unselectedList: ActivityCategory[];
    selectedList: ActivityCategory[];
  } = {
    unselectedList: [],
    selectedList: [],
  };

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;

  ngOnInit(): void {
    this.fetchAllAndComputeSelectedList()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  compareFn = (option: Base, value: Base) =>
    option ? option?.id === value?.id : option === value;

  categorySelection(newSelectedCategoriesList: ActivityCategory[]): void {
    // Keep the UI selection in sync so cancel-in-modal preserves what the user built.
    this.selectedCategories = newSelectedCategoriesList;
    // Always diff against the saved server state, not the live selection.
    this.listToUpdate =
      this.activityCategorySelectionService.computeListToUpdate(
        this.savedCategories,
        newSelectedCategoriesList,
      );
    this.formDirty.emit(
      this.listToUpdate.selectedList.length > 0 ||
        this.listToUpdate.unselectedList.length > 0,
    );
  }

  resetToSaved(): void {
    this.selectedCategories = [...this.savedCategories];
    this.listToUpdate = { unselectedList: [], selectedList: [] };
    this.formDirty.emit(false);
    this.cdr.markForCheck();
  }

  async submitWithConfirmation(): Promise<void> {
    const added = this.listToUpdate.selectedList;
    const removed = this.listToUpdate.unselectedList;
    if (!added.length && !removed.length) return;

    const modalRef =
      this.nzModalService.create<ActivityCategoryConfirmModalComponent>({
        nzTitle: this.translate.instant(
          'page.settings.company_settings.activity_categories_confirm_title',
        ),
        nzContent: ActivityCategoryConfirmModalComponent,
      });

    if (modalRef.componentInstance) {
      modalRef.componentInstance.added = added;
      modalRef.componentInstance.removed = removed;
      modalRef.componentInstance.handbookUrl = this.translate.instant(
        'page.settings.company_settings.activity_categories_handbook_url',
      );
    }

    const confirmed = await firstValueFrom(modalRef.afterClose);
    if (confirmed) {
      this.submit(this.listToUpdate);
    }
  }

  submit(updateList: {
    unselectedList: ActivityCategory[];
    selectedList: ActivityCategory[];
  }): void {
    this.isSaving = true;
    this.activitiesService
      .updateActivityCategories(
        [...updateList.unselectedList, ...updateList.selectedList].map(
          (activity) => ({
            isEnabled: activity.isEnabled,
            activityCategoryId: activity.id as string,
          }),
        ),
      )
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        error: () => {
          this.messageService.error(this.translate.instant('general.error'));
        },
        next: () => {
          // Advance the saved baseline to match what was just persisted.
          this.savedCategories =
            this.activityCategorySelectionService.removeFromList(
              this.savedCategories,
              updateList.unselectedList,
            );
          this.savedCategories = [
            ...this.savedCategories,
            ...updateList.selectedList,
          ];
          this.selectedCategories = [...this.savedCategories];
          this.listToUpdate = { unselectedList: [], selectedList: [] };
          this.formDirty.emit(false);
        },
      });
  }

  private fetchAllAndComputeSelectedList(): Observable<ActivityCategory[]> {
    this.isLoading = true;
    return this.activitiesService.getAllActivityCategories().pipe(
      map((activityCategory: ActivityCategory[]) => {
        activityCategory.sort((a, b) =>
          this.translateActivityCategoryService
            .resolve(a)
            .localeCompare(this.translateActivityCategoryService.resolve(b)),
        );
        return activityCategory;
      }),
      tap((categories) => {
        this.availableCategories = categories;
        this.savedCategories = categories.filter(
          (category) => category.isEnabled,
        );
        this.selectedCategories = [...this.savedCategories];
        this.cdr.markForCheck();
      }),
      catchError(() => {
        this.messageService.error(this.translate.instant('general.error'));
        return of([]);
      }),
      finalize(() => (this.isLoading = false)),
    );
  }
}
